import React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableCaption,
  TableContainer,
  Flex,
  Heading,
  Stack,
  Text,
  useColorModeValue,
  Image,
  Box,
  Select,
  Button,
} from "@chakra-ui/react";
import { photoBaseURL } from "../../service/api";
import { useGetState } from "../../context/useGetState";
import { useState } from "react";

const calculateCumulativeTotal = (data) => {
  let cumulativeTotal = 0;

  if (data?.length > 0) {
    return data?.map((earn) => {
      const entryAmount = parseFloat(earn?.amount);
      cumulativeTotal += entryAmount;
      return cumulativeTotal;
    });
  } else {
    return cumulativeTotal;
  }
};

const EarningsTable = ({ data }) => {
  const inputBgColor = useColorModeValue("#fff", "#081b2e");
  const [selectedOption, setSelectedOption] = useState("7"); // Default option is "Last 7 days"
  const [currentPage, setCurrentPage] = useState(0);
  const { state } = useGetState();
  const userInfo = state?.userInfo;
  const userImg = `${photoBaseURL}${userInfo?.photo}`;
  const totalAmount =
    data?.length > 0
      ? data?.reduce((sum, entry) => sum + parseFloat(entry.amount), 0)
      : 0;
  const filterDataBySelectedOption = (entry) => {
    const currentDate = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(currentDate.getDate() - parseInt(selectedOption)); //select option value is gotten here
    const entryDate = new Date(entry.created_at);
    return entryDate >= cutoffDate;
  };

  //filtered data based on select-input
  let filteredData = data?.filter(filterDataBySelectedOption);

  //for resultingAmount
  const cumulativeTotals = calculateCumulativeTotal(filteredData);

  //for prev and next
  const rowsPerPage = 5;
  const totalPages = Math.ceil(filteredData?.length / rowsPerPage);
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  //slice data based on page index
  filteredData = filteredData?.slice(startIndex, endIndex);

  const formatDate = (dateString) => {
    const formattedDate = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(dateString));
    return formattedDate;
  };

  const inputStyles = {
    p: "5px",
    borderRadius: "5px",
    boxShadow: "3px 8px 13px 0px #8080800f",
    bg: inputBgColor,
    w: "100%",
    fontSize: ".9em",
  };

  const noDataStyles = {
    p: "10px",
    borderRadius: "5px",
    boxShadow: "3px 8px 13px 0px #8080800f",
    bg: inputBgColor,
    w: "100%",
    fontSize: ".9em",
  };

  return (
    <>
      {filteredData?.length > 0 ? (
        <>
          <Flex justifyContent="end" style={{ marginBottom: "15px" }}>
            <Stack>
              <Select
                {...inputStyles}
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
              >
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
                <option value="60">Last 60 days</option>
              </Select>
            </Stack>
          </Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            style={{ marginBottom: "15px" }}
          >
            <Stack direction="row" width="80%" justifyContent="space-between">
              <Text fontSize="1.5em">Most Recent Earnings</Text>
              <Text fontSize="1.5em">Revenue (${totalAmount})</Text>
            </Stack>
          </Flex>
          <TableContainer>
            <Table variant="simple">
              <TableCaption display={"none"}>
                Imperial to metric conversion factors
              </TableCaption>
              <Thead display={"none"}>
                <Tr>
                  <Th>To convert</Th>
                  <Th>into</Th>
                  <Th isNumeric>multiply by</Th>
                </Tr>
              </Thead>

              <Tbody>
                {filteredData?.map((earn, index) => (
                  <Box
                    key={index}
                    border="0.5px solid rgba(0, 0, 0, 0.1)" // Add the border style
                    boxShadow="0px 1px 4px rgba(0, 0, 0, 0.1)" // Add the box shadow style
                    borderRadius="5px"
                    bg={inputBgColor}
                    mb="5px" // Add margin at the bottom
                  >
                    <Tr>
                      <Td w="50%">
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          w="100%"
                          justifyContent="space-between"
                        >
                          <Stack>
                            <Image
                              w="45px"
                              minW="45px"
                              minH="45px"
                              h="45px"
                              borderRadius="5%"
                              objectFit="cover"
                              border="2px solid #fff"
                              src={userImg}
                              alt={userInfo?.name}
                            />
                          </Stack>
                          <Stack w="100%">
                            <Text>
                              Lorem Ipsum dolor Proma Glaria Acedet <br />
                              Acedet Proma Glaria <br />
                              <Text>
                                <strong>
                                  Date: {`${formatDate(earn?.created_at)}`}
                                </strong>
                              </Text>
                            </Text>
                          </Stack>
                        </Stack>
                      </Td>
                      <Td w="50%" className="amount">
                        ${`${earn?.amount}`}
                      </Td>
                      <Td
                        w="100%"
                        className="resultingAmount"
                      >{`$${cumulativeTotals[index]}`}</Td>
                    </Tr>
                  </Box>
                ))}
              </Tbody>

              <Tfoot display={"none"}>
                <Tr>
                  <Th>To convert</Th>
                  <Th>into</Th>
                  <Th isNumeric>multiply by</Th>
                </Tr>
              </Tfoot>

              <Flex justifyContent="center" mt="10px">
                <Button
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  mr="10px"
                  bg="#1392E6"
                >
                  Previous
                </Button>
                <Button
                  disabled={currentPage === totalPages - 1}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  bg="#1392E6"
                >
                  Next
                </Button>
              </Flex>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Stack
          direction={"column"}
          mb="20px"
          justifyContent={"center"}
          {...noDataStyles}
        >
          <Heading fontSize="1.5em">Most Recent Earnings</Heading>

          <Text textAlign={"center"}>No data at the moment</Text>
        </Stack>
      )}
    </>
  );
};

export default EarningsTable;
